import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Avatar,
  Button,
  List,
  useTheme,
  Divider,
  CircularProgress,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { newsService } from '@/services/news/newsService';
import { feedService } from '@/services/feed/feedService';
import { News, Feed } from '@/constants/types';
import moment from 'moment';

interface RightSidebarProps {
  width: number;
}

export const RightSidebar: React.FC<RightSidebarProps> = ({ width }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [topPosts, setTopPosts] = useState<Feed[]>([]);
  const [topNews, setTopNews] = useState<News[]>([]);
  const [loadingPosts, setLoadingPosts] = useState(false);
  const [loadingNews, setLoadingNews] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoadingPosts(true);
    setLoadingNews(true);
    try {
      const postsRes = await feedService.getTopSuperAdminPosts();
      if (postsRes.success) {
        setTopPosts(postsRes.data);
      }

      const newsRes = await newsService.getTopNews();
      if (newsRes.success) {
        setTopNews(newsRes.data);
      }
    } catch (error) {
      console.error('Failed to load sidebar data:', error);
    } finally {
      setLoadingPosts(false);
      setLoadingNews(false);
    }
  };

  const renderTitle = (title: string) => (
    <Typography
      variant="subtitle2"
      sx={{
        fontWeight: 600,
        mb: 1.5,
        color: 'text.primary',
        fontSize: '0.9rem',
        // textTransform: 'uppercase',
        letterSpacing: '0.5px',
      }}
    >
      {title}
    </Typography>
  );

  return (
    <Box
      sx={{
        position: 'sticky',
        top: 64,
        height: `calc(100vh - 72px)`,
        flexShrink: 0,
        width,
        zIndex: theme.zIndex.drawer,
        bgcolor: 'background.default',
        overflow: 'hidden',
      }}
    >
      <Box
        sx={{
          width: '100%',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
        }}
      >
        {/* Top 5 Posts by Super Admin */}
        <Paper
          elevation={0}
          sx={{
            p: 2,
            borderRadius: 2,
            border: `1px solid ${theme.palette.divider}`,
            bgcolor: 'background.paper',
            display: 'flex',
            flexDirection: 'column',
            maxHeight: '50%',
          }}
        >
          {renderTitle('Top Posts')}
          
          {loadingPosts ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
              <CircularProgress size={24} />
            </Box>
          ) : (
            <List sx={{ p: 0, flex: 1, overflow: 'auto' }}>
              {topPosts.length > 0 ? topPosts.map((post, index) => (
                <Box
                  key={post._id}
                  onClick={() => navigate(`/talky?feedId=${post._id}`)}
                  sx={{
                    // py: 1,
                    cursor: 'pointer',
                   
                  }}
                >
                  <Box 
                    sx={{ 
                      '&:hover': {
                        bgcolor: 'action.hover',
                      },
                    }}>
                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 0.5 }}>
                    <Avatar 
                      src={post.userId?.profilePicture?.url} 
                      sx={{ width: 24, height: 24, fontSize: '0.7rem' }}
                    >
                      {post.userId?.name.charAt(0)}
                    </Avatar>
                    <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.primary' }} noWrap>
                      {post.userId?.name}
                    </Typography>
                  </Box>
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 500,
                      fontSize: '0.8rem',
                      color: 'text.primary',
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden',
                      lineHeight: 1.4,
                    }}
                  >
                    {post.title || post.content.substring(0, 60) + '...'}
                  </Typography>
                  <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.7rem' }}>
                    {moment(post.createdAt).fromNow()}
                  </Typography>
                  </Box>
                  {index < topPosts.length - 1 && <Divider sx={{ my: 1 }} />}
                </Box>
              )) : (
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>No posts available</Typography>
              )}
            </List>
          )}
        </Paper>

        {/* Top 5 News */}
        <Paper
          elevation={0}
          sx={{
            p: 2,
            borderRadius: 2,
            border: `1px solid ${theme.palette.divider}`,
            bgcolor: 'background.paper',
            display: 'flex',
            flexDirection: 'column',
            flex: 1,
            overflow: 'hidden',
          }}
        >
          {renderTitle('Latest News')}
          
          {loadingNews ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
              <CircularProgress size={24} />
            </Box>
          ) : (
            <>
              <List sx={{ p: 0, flex: 1, overflow: 'auto' }}>
                {topNews.length > 0 ? topNews.map((news, index) => (
                  <Box
                    key={news._id}
                    onClick={() => navigate(`/news/${news._id}`)}
                    sx={{
                      // py: 1,
                      cursor: 'pointer',
                      
                    }}
                  >
                    <Box 
                      sx={{
                        '&:hover': {
                          bgcolor: 'action.hover',
                        },
                      }}>
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 600,
                        fontSize: '0.85rem',
                        color: 'text.primary',
                        mb: 0.5,
                      }}
                    >
                      {news.title}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        color: 'text.secondary',
                        fontSize: '0.8rem',
                        lineHeight: 1.4,
                      }}
                    >
                      {(() => {
                        const text = news.summary || (news.content ? news.content.replace(/<[^>]*>/g, '') : '');
                        const limit = 60;
                        if (text.length <= limit) return text;
                        return (
                          <>
                            {text.substring(0, limit)}...
                            <Box 
                              component="span" 
                              sx={{ 
                                color: 'primary.main', 
                                ml: 0.5, 
                                fontWeight: 500,
                                '&:hover': { textDecoration: 'underline' }
                              }}
                            >
                              Read More
                            </Box>
                          </>
                        );
                      })()}
                    </Typography>
                    <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.7rem', display: 'block', mt: 0.5 }}>
                      {moment(news.createdAt).fromNow()}
                    </Typography>
                    </Box>
                    {index < topNews.length - 1 && <Divider sx={{ my: 1 }} />}
                  </Box>
                )) : (
                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>No news available</Typography>
                )}
              </List>
              
              <Button
                fullWidth
                size="small"
                onClick={() => navigate('/news')}
                sx={{
                  mt: 1,
                  textTransform: 'none',
                  fontSize: '0.8rem',
                  fontWeight: 600,
                  color: 'primary.main',
                  '&:hover': { bgcolor: 'rgba(10, 102, 194, 0.08)' },
                }}
              >
                View all news
              </Button>
            </>
          )}
        </Paper>
        {/* <Paper
          elevation={0}
          sx={{
            p: 2,
            flex: 1,
            borderRadius: 2,
            border: `1px solid ${theme.palette.divider}`,
            bgcolor: 'background.paper',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          }}
        >
          <Typography
            variant="subtitle2"
            sx={{
              fontWeight: 600,
              mb: 1.5,
              flexShrink: 0,
              color: 'text.primary',
              fontSize: '0.9rem',
            }}
          >
            People you may know
          </Typography>

         <List sx={{
            p: 0,
            flex: 1,
            overflow: 'auto',
            '&::-webkit-scrollbar': {
              width: '6px',
            },
            '&::-webkit-scrollbar-track': {
              background: 'transparent',
            },
            '&::-webkit-scrollbar-thumb': {
              background: theme.palette.divider,
              borderRadius: '3px',
            },
            '&::-webkit-scrollbar-thumb:hover': {
              background: theme.palette.text.secondary,
            },
          }}>
  {suggestions.map((person, index) => (
    <React.Fragment key={person._id}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          py: 1.5,
          px: 0.5,
          borderRadius: 1,
          cursor: 'pointer',
          '&:hover': {
            bgcolor: theme.palette.mode === 'light' ? '#f3f2ef' : 'action.hover',
          },
        }}
      >
        
        <Box
          onClick={() => navigate('/profile/' + person._id)}
          sx={{ position: 'relative', flexShrink: 0 }}
        >
          {person.isLookingForJob && (
            <Box
              sx={{
                position: 'absolute',
                top: -2,
                left: -2,
                right: -2,
                bottom: -2,
                borderRadius: '50%',
                background: '#0a66c2',
                zIndex: 0,
              }}
            />
          )}
          <Avatar
            src={person.profilePictureUrl || undefined}
            sx={{
              width: 48,
              height: 48,
              bgcolor: '#0a66c2',
              fontSize: '1rem',
              fontWeight: 600,
              position: 'relative',
              zIndex: 1,
              border: person.isLookingForJob ? `2px solid ${theme.palette.background.paper}` : 'none',
            }}
          >
            {person.name.charAt(0)}
          </Avatar>
          {person.isLookingForJob && (
            <Box
              sx={{
                position: 'absolute',
                bottom: -4,
                left: '50%',
                transform: 'translateX(-50%)',
                background: '#0a66c2',
                color: 'white',
                fontSize: '6px',
                fontWeight: 700,
                padding: '1px 5px',
                borderRadius: '6px',
                whiteSpace: 'nowrap',
                zIndex: 2,
              }}
            >
              #OpenToWork
            </Box>
          )}
        </Box>

        <Box
          onClick={() => navigate('/profile/' + person._id)}
          sx={{ flex: 1, minWidth: 0 }}
        >
          <Typography
            variant="body2"
            sx={{
              fontWeight: 600,
              fontSize: '0.85rem',
              color: 'text.primary',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              '&:hover': { color: '#0a66c2' },
            }}
          >
            {person.name}
          </Typography>
        </Box>

        <Button
          size="small"
          variant="outlined"
          disabled={person.hasRequestPending || localPendingRequests.has(person._id)}
          onClick={(e) => {
            e.stopPropagation();
            handleFollowSuggestion(person._id);
          }}
          sx={{
            flexShrink: 0,
            px: 1.5,
            py: 0.5,
            borderRadius: 5,
            textTransform: 'none',
            fontSize: '0.75rem',
            fontWeight: 600,
            minWidth: 'auto',
            borderColor: '#0a66c2',
            color: '#0a66c2',
            '&:hover': {
              borderColor: '#004182',
              bgcolor: 'rgba(10, 102, 194, 0.08)',
            },
            '&.Mui-disabled': {
              borderColor: theme.palette.divider,
              color: 'text.disabled',
            },
          }}
        >
          {person.hasRequestPending || localPendingRequests.has(person._id) ? 'Pending' : '+ Follow'}
        </Button>
      </Box>

      {index < suggestions.length - 1 && (
        <Divider sx={{ my: 0.5 }} />
      )}
    </React.Fragment>
  ))}
</List>


          <Button
            variant="text"
            size="small"
            onClick={handleViewMore}
            sx={{
              mt: 1,
              flexShrink: 0,
              color: '#0a66c2',
              fontSize: '0.8rem',
              textTransform: 'none',
              fontWeight: 600,
              p: 0,
              '&:hover': {
                bgcolor: 'transparent',
                textDecoration: 'underline',
              },
            }}
          >
            View all recommendations →
          </Button>
        </Paper> */}
      </Box>
    </Box>
  );
};
